'use client';

import { useMemo } from 'react';

import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { usePathLocale } from '../../../../hooks/use-path-locale';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';
import { QuoteProductPriceItem } from '../models/quote-product-price-item.model';

interface Props {
   product: QuoteProductPriceItem;
   productDetailBaseRoute: string;
   documentsAnchor: string;
   channelName: string;
}

export const QuoteProductHeader = ({ product, productDetailBaseRoute, documentsAnchor, channelName }: Props) => {
   const translate = useTranslations();
   const locale = usePathLocale();

   const showLink = !!product.slug && !!productDetailBaseRoute;
   const productDetailRoute = { url: productDetailBaseRoute, noFollow: true };

   const slug = useMemo(() => {
      if (!product.slug) return undefined;

      const searchParams = new URLSearchParams();
      searchParams.set('from', 'quote');
      if (product.colorId != null) searchParams.set('colorId', String(product.colorId));
      if (product.producerColorCode) searchParams.set('colorCode', product.producerColorCode);
      return `${product.slug}?${searchParams.toString()}`;
   }, [product]);

   const referenceDocumentsSection = (
      <Box direction="row" gap="sm">
         <Typography variant="small">
            {translate('prices.bussiness-context-reference', { businessContextName: channelName })} {product.businessContextReference || '-'}
         </Typography>
         <Box margin={{ y: '2xs' }}>
            <Divider orientation="vertical" selfStretch />
         </Box>
         {showLink && slug ? (
            <DynamicPageLink id={`${product.id}-documents`} baseRoute={productDetailRoute} locale={locale} slug={slug} anchor={documentsAnchor}>
               <Box gap="2xs" items="center">
                  <Icon icon="file-alt" color="inherit" size="small" />
                  <Typography variant="small" color="primary" underline>
                     {translate('prices.related-documents')}
                  </Typography>
               </Box>
            </DynamicPageLink>
         ) : (
            <Typography variant="small">-</Typography>
         )}
      </Box>
   );

   return (
      <Box direction="column" gap="sm">
         <Box gap="md" items="center">
            <BoxItem>
               <Avatar shape="square" size="large" src={product.logoUrl} title={product.brandName} />
            </BoxItem>
            <BoxItem grow={1} basis={0}>
               <Box direction="column" gap="xs">
                  {product.polymerType && (
                     <Badge variant="neutral" weight="regular">
                        {product.polymerType}
                     </Badge>
                  )}
                  {showLink && slug ? (
                     <DynamicPageLink id={slug} baseRoute={productDetailRoute} locale={locale} slug={slug}>
                        <Typography variant="body-highlight" color="inherit">
                           {product.description}
                        </Typography>
                     </DynamicPageLink>
                  ) : (
                     <Typography variant="body-highlight">{product.description || '-'}</Typography>
                  )}
                  <BreakpointSwitch excludeViews={['phone']}>{referenceDocumentsSection}</BreakpointSwitch>
               </Box>
            </BoxItem>
         </Box>
         <BreakpointSwitch includeViews={['phone']}>{referenceDocumentsSection}</BreakpointSwitch>
      </Box>
   );
};
